/**
 * Baut HTML-Fragmente aus Share-API-Daten und ersetzt {{PLATZHALTER}} im Template.
 *
 * Template „ambition-default“: TOP_HEADER_HTML, MIDDLE_BODY_HTML, CONTACT_ROW_HTML, PAGE_TITLE, …
 * Template „symplr“: MAIN_BODY_HTML, CONTACT_ROW_HTML, PAGE_TITLE, …
 * Neues Template: Datei + Eintrag in scoreCardTemplates.ts; bei abweichendem Layout hier einen Zweig ergänzen.
 */
import { colors } from '../../styles/theme.js';
import type { Rating, IScoreCard, IQuickWin } from '../../../shared/types.js';
import type { ShareContact } from './shareContact.js';

export interface SharedScoreCardData {
  score_card: IScoreCard & { Scan?: { url: string; user?: { name: string } } };
  quick_wins: IQuickWin[];
  disclaimer: string | null;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function scoreToColor(score: number): string {
  if (score >= 80) return colors.scoreGreen;
  if (score >= 50) return colors.scoreYellow;
  return colors.scoreRed;
}

function sanitizeDisplayUrl(rawUrl: string): string {
  const trimmed = rawUrl.trim();
  if (!trimmed) return '';

  const withoutProtocol = trimmed.replace(/^https?:\/\//i, '');
  return withoutProtocol.replace(/\/+$/, '');
}

/** Halbkreis-Gauge als SVG-String (gleiche Geometrie wie ScoreGaugeSemi). */
function semiGaugeSvgHtml(score: number, label: string, size = 130): string {
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const halfCircumference = Math.PI * radius;
  const progress = (Math.min(Math.max(score, 0), 100) / 100) * halfCircumference;
  const cy = size / 2;
  const svgH = cy + strokeWidth / 2;
  const color = scoreToColor(score);
  const labelEsc = escapeHtml(label);
  const scoreRounded = Math.round(score);

  return `<div class="sc-gauge-wrap">
  <svg width="${size}" height="${svgH}" viewBox="0 0 ${size} ${svgH}" style="display:block;overflow:visible" aria-hidden="true">
    <path d="M ${strokeWidth / 2} ${cy} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${cy}"
      fill="none" stroke="#e0e0e0" stroke-width="${strokeWidth}" stroke-linecap="round" />
    <path d="M ${strokeWidth / 2} ${cy} A ${radius} ${radius} 0 0 1 ${size - strokeWidth / 2} ${cy}"
      fill="none" stroke="${color}" stroke-width="${strokeWidth}" stroke-linecap="round"
      stroke-dasharray="${progress} ${halfCircumference}" />
    <text x="${size / 2}" y="${cy - 4}" text-anchor="middle" dominant-baseline="auto"
      style="font-size:${size * 0.2}px;font-weight:700;fill:${colors.black};font-family:Poppins,sans-serif">${scoreRounded}</text>
    <text x="${size / 2}" y="${cy + size * 0.11}" text-anchor="middle" dominant-baseline="auto"
      style="font-size:${size * 0.09}px;font-weight:400;fill:${colors.black60};font-family:Poppins,sans-serif">/100</text>
  </svg>
  <div class="sc-gauge-label">${labelEsc}</div>
</div>`;
}

function buildGaugesInner(
  categories: { category_id: string; display_name?: string; category_name?: string; score: number }[],
  scanPending = false,
): string {
  if (categories.length === 0) {
    const msg = scanPending
      ? 'Scan läuft – Ergebnisse erscheinen automatisch …'
      : 'Keine Kategorien';
    return `<div class="sc-gauges-empty">${escapeHtml(msg)}</div>`;
  }
  return categories
    .map((cat, idx) => {
      const label = cat.display_name ?? cat.category_name ?? '';
      const border = idx < categories.length - 1 ? `border-right:1px solid ${colors.black}` : '';
      return `<div class="sc-gauge-cell" style="${border}">${semiGaugeSvgHtml(cat.score, label)}</div>`;
    })
    .join('');
}

function buildCombinedQuickWinsInner(quickWins: IQuickWin[], scanPending = false): string {
  if (quickWins.length === 0) {
    const msg = scanPending
      ? 'Scan läuft – Quick-Wins erscheinen nach Abschluss …'
      : 'Keine Quick-Wins gefunden';
    return `<div class="sc-qw-empty">${escapeHtml(msg)}</div>`;
  }
  return quickWins
    .map((qw, idx) => {
      const border = idx < quickWins.length - 1 ? `border-bottom:1px solid ${colors.black}` : '';
      const title = escapeHtml(qw.title);
      const desc = qw.description ? escapeHtml(qw.description) : '';
      const category = (qw as unknown as { category?: string }).category;
      const badge = category ? `<span class="sc-qw-badge">${escapeHtml(category)}</span> ` : '';
      return `<div class="sc-qw-row" style="${border}">
  <div class="sc-qw-num">${idx + 1}</div>
  <div class="sc-qw-text">
    <div class="sc-qw-title">${badge}${title}</div>
    ${desc ? `<div class="sc-qw-desc">${desc}</div>` : ''}
  </div>
</div>`;
    })
    .join('');
}

function buildTopHeaderHtml(_catCount: number, quickWinsHeader: string): string {
  return `<div class="sc-headleft" style="grid-column:1;border-right:1px solid ${colors.white}">Domain-Scoring-Card</div>
<div class="sc-headright" style="grid-column:2">${escapeHtml(quickWinsHeader)}</div>`;
}

function buildMiddleBodyHtml(
  _catCount: number,
  categories: { category_id: string; display_name?: string; category_name?: string; score: number }[],
  quickWins: IQuickWin[],
  scanPending = false,
): string {
  const emptyMsg = scanPending
    ? `<div class="sc-qw-empty">${escapeHtml('Scan läuft – Quick-Wins erscheinen nach Abschluss …')}</div>`
    : `<div class="sc-qw-empty">${escapeHtml('Keine Quick-Wins gefunden')}</div>`;

  const qwCount = quickWins.length;

  const qwItems = quickWins.slice(0, 3).map((qw, i) => {
    return `<div style="display:flex;flex-direction:column">${buildSingleQuickWinHtml(qw, i)}</div>`;
  }).join('');

  const qwContent = qwCount === 0
    ? emptyMsg
    : `<div style="display:flex;flex-direction:column;flex:1;justify-content:space-evenly">${qwItems}</div>`;

  const qwCell = `<div class="sc-qw-col" style="grid-column:2;grid-row:2 / span 2;display:flex;flex-direction:column">
  <div class="sc-qw-section">${qwContent}</div>
</div>`;

  return `<div class="sc-gauges" style="grid-column:1;display:flex;border-right:1px solid ${colors.black};min-height:220px">${buildGaugesInner(categories, scanPending)}</div>
${qwCell}`;
}

function buildSingleQuickWinHtml(qw: IQuickWin, idx: number): string {
  const title = escapeHtml(qw.title);
  const desc = qw.description ? escapeHtml(qw.description) : '';
  const category = (qw as unknown as { category?: string }).category;
  const badge = category ? `<span class="sc-qw-badge">${escapeHtml(category)}</span> ` : '';
  return `<div class="sc-qw-row">
  <div class="sc-qw-num">${idx + 1}</div>
  <div class="sc-qw-text">
    <div class="sc-qw-title">${badge}${title}</div>
    ${desc ? `<div class="sc-qw-desc">${desc}</div>` : ''}
  </div>
</div>`;
}

function buildContactRowAmbition(_catCount: number, c: ShareContact): string {
  const telHref = c.phone.replace(/\s/g, '');
  const top = `border-top:1px solid ${colors.black}`;
  return `<div class="sc-contact-left" style="grid-column:1;${top};display:flex;flex-direction:column;justify-content:center;gap:12px;padding:20px 24px;border-right:1px solid ${colors.black};min-height:220px">
    <div class="sc-screening-hint" style="font-size:0.75rem;color:#4a4a4a;line-height:1.4">
      Dieses Ergebnis basiert auf einem oberfl&auml;chlichen Screening.<br/>
      Eine fundierte Bewertung des Tracking-Setups ist erst mit einem vollst&auml;ndigen Audit m&ouml;glich.
    </div>
    <div style="display:flex;align-items:center;gap:20px">
      <img class="sc-contact-img" src="${escapeHtml(c.imageUrl)}" alt="${escapeHtml(c.name)}" width="72" height="72"
        onerror="this.style.display='none'" />
      <div>
        <div class="sc-contact-lead"><span class="sc-contact-q">Fragen?</span> <span class="sc-contact-amiri">Let's Talk!</span></div>
        <div class="sc-contact-name">${escapeHtml(c.name)}</div>
        <div class="sc-contact-role">${escapeHtml(c.role)}</div>
        <div class="sc-contact-links">
          <a class="sc-contact-a" href="mailto:${escapeHtml(c.email)}">${escapeHtml(c.email)}</a>
          <span class="sc-contact-sep"> · </span>
          <a class="sc-contact-a" href="tel:${escapeHtml(telHref)}">${escapeHtml(c.phone)}</a>
        </div>
      </div>
    </div>
  </div>`;
}

function buildContactMinimal(c: ShareContact): string {
  const telHref = c.phone.replace(/\s/g, '');
  return `<section class="min-contact">
  <h3 class="min-h3">Kontakt</h3>
  <p><strong>${escapeHtml(c.name)}</strong> — ${escapeHtml(c.role)}</p>
  <p class="min-contact-links">
    <span class="min-contact-item">
      <svg class="min-contact-icon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" />
      </svg>
      <a href="mailto:${escapeHtml(c.email)}">${escapeHtml(c.email)}</a>
    </span>
    <span class="min-contact-sep"> · </span>
    <span class="min-contact-item">
      <svg class="min-contact-icon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1C10.07 21 3 13.93 3 5c0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.24.2 2.45.57 3.57.11.35.03.75-.25 1.02l-2.2 2.2z" />
      </svg>
      <a href="tel:${escapeHtml(telHref)}">${escapeHtml(c.phone)}</a>
    </span>
  </p>
</section>`;
}

function buildContactSymplrStatic(): string {
  return `<section class="min-contact">
  <h3 class="min-h3">Kontakt</h3>
  <p><strong>Karin Pawlik-Passade</strong> - Team Lead symplr &amp; New Business</p>
  <p>
    <span class="min-contact-item">
      <svg class="min-contact-icon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1C10.07 21 3 13.93 3 5c0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.24.2 2.45.57 3.57.11.35.03.75-.25 1.02l-2.2 2.2z" />
      </svg>
      <a href="tel:+4915159159038">+49 151 591 590 38</a>
    </span>
    <span class="min-contact-sep"> - </span>
    <span class="min-contact-item">
      <svg class="min-contact-icon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z" />
      </svg>
      <a href="mailto:k.pawlik-passade@symplr.de">k.pawlik-passade@symplr.de</a>
    </span>
  </p>
</section>`;
}

function buildMainBodyMinimal(
  categories: { display_name?: string; category_name?: string; score: number }[],
  quickWins: IQuickWin[],
  scanPending = false,
): string {
  const catLines =
    categories.length === 0
      ? `<li>${scanPending ? 'Scan läuft – Ergebnisse erscheinen automatisch …' : 'Keine Kategorien'}</li>`
      : categories
          .map(
            (cat) =>
              `<li><strong>${escapeHtml(cat.display_name ?? cat.category_name ?? '')}</strong>: ${Math.round(cat.score)} / 100</li>`,
          )
          .join('');
  const qwEmpty = scanPending
    ? '<p>Scan läuft – Quick-Wins erscheinen nach Abschluss …</p>'
    : '<p>Keine Quick-Wins gefunden</p>';
  const qwSection = `<section class="min-section"><h2 class="min-h2">Quick-Wins &amp; Hinweise</h2>${
    quickWins.length === 0
      ? qwEmpty
      : `<ol>${quickWins.map((qw) => `<li><strong>${escapeHtml(qw.title)}</strong>${qw.description ? ` — ${escapeHtml(qw.description)}` : ''}</li>`).join('')}</ol>`
  }</section>`;
  return `<section class="min-section"><h2 class="min-h2">Kategorien</h2><ul class="min-ul">${catLines}</ul></section>
${qwSection}`;
}

function buildMainBodySymplr(
  categories: { category_id: string; display_name?: string; category_name?: string; score: number }[],
  quickWins: IQuickWin[],
  scanPending = false,
): string {
  const gauges =
    categories.length === 0
      ? `<div class="sp-gauge-empty">${scanPending ? 'Scan läuft – Ergebnisse erscheinen automatisch …' : 'Keine Kategorien'}</div>`
      : categories
          .map((cat) => {
            const label = cat.display_name ?? cat.category_name ?? '';
            return `<div class="sp-gauge-item">${semiGaugeSvgHtml(cat.score, label, 142)}</div>`;
          })
          .join('');

  const qwEmptyMsg = scanPending
    ? 'Scan läuft – Quick-Wins erscheinen nach Abschluss …'
    : 'Keine Quick-Wins gefunden';
  const quickWinsList =
    quickWins.length === 0
      ? `<p class="sp-list-empty">${escapeHtml(qwEmptyMsg)}</p>`
      : `<ol class="sp-list">${quickWins.map((qw) => `<li><strong>${escapeHtml(qw.title)}</strong>${qw.description ? `<br /><span>${escapeHtml(qw.description)}</span>` : ''}</li>`).join('')}</ol>`;

  const textBoard = `<div class="sp-text-board">
    <section class="sp-text-panel"><h2 class="sp-panel-title">Quick-Wins &amp; Hinweise</h2>${quickWinsList}</section>
  </div>`;

  return `<section class="sp-main-grid">
  <div class="sp-gauge-board">
    <h2 class="sp-panel-title">Kategorie Scores</h2>
    <div class="sp-gauge-grid">${gauges}</div>
  </div>
  ${textBoard}
</section>`;
}

function applyPlaceholders(template: string, map: Record<string, string>): string {
  let out = template;
  for (const [k, v] of Object.entries(map)) {
    out = out.split(`{{${k}}}`).join(v);
  }
  return out;
}

export function renderScoreCardHtml(
  templateSource: string,
  data: SharedScoreCardData,
  templateId: string,
  contact: ShareContact,
  options?: { scanPending?: boolean; evaluating?: boolean },
): string {
  const { score_card, quick_wins, disclaimer } = data;
  const pending = options?.scanPending === true || options?.evaluating === true;
  const scannedUrl = (score_card as unknown as { Scan?: { url: string } }).Scan?.url ?? '';
  const cleanedScannedUrl = sanitizeDisplayUrl(scannedUrl);
  const scores = score_card.scores_json as {
    categories: { category_id: string; category_name?: string; display_name?: string; score: number; rating: Rating }[];
  } | null;
  const categories = pending ? [] : (scores?.categories ?? []);
  const quickWins = pending ? [] : quick_wins.slice(0, 3);
  const catCount = Math.max(categories.length, 1);

  const disclaimerSection =
    disclaimer && disclaimer.trim()
      ? `<div class="sc-disclaimer-wrap"><p class="sc-disclaimer">${escapeHtml(disclaimer)}</p></div>`
      : '';

  const common: Record<string, string> = {
    SCANNED_URL: escapeHtml(cleanedScannedUrl),
    DISCLAIMER_SECTION_HTML: disclaimerSection,
  };

  if (templateId === 'symplr') {
    return applyPlaceholders(templateSource, {
      ...common,
      PAGE_TITLE: 'symplr Scorecard',
      MAIN_BODY_HTML: buildMainBodySymplr(categories, quickWins, pending),
      CONTACT_ROW_HTML: buildContactSymplrStatic(),
    });
  }

  if (templateId === 'compact') {
    return applyPlaceholders(templateSource, {
      ...common,
      PAGE_TITLE: 'Compact (Test)',
      MAIN_BODY_HTML: buildMainBodyMinimal(categories, quickWins, pending),
      CONTACT_ROW_HTML: buildContactMinimal(contact),
    });
  }

  // ambition-default (und Fallback)
  const gridCols = `minmax(0,3fr) minmax(0,1.2fr)`;
  const gridRows = 'auto minmax(220px,220px) minmax(220px,220px)';

  let html = applyPlaceholders(templateSource, {
    ...common,
    PAGE_TITLE: 'Domain Screening',
    GRID_COLS: gridCols,
    GRID_ROWS: gridRows,
    TOP_HEADER_HTML: buildTopHeaderHtml(catCount, 'Quick-Wins & Hinweise'),
    MIDDLE_BODY_HTML: buildMiddleBodyHtml(catCount, categories, quickWins, pending),
    CONTACT_ROW_HTML: buildContactRowAmbition(catCount, contact),
  });

  if (options?.evaluating) {
    html = html
      .replace(/Scan läuft – Ergebnisse erscheinen automatisch …/g, 'Daten werden ausgewertet — Ergebnisse erscheinen in wenigen Sekunden …')
      .replace(/Scan l&auml;uft – Ergebnisse erscheinen automatisch &hellip;/g, 'Daten werden ausgewertet &mdash; Ergebnisse erscheinen in wenigen Sekunden &hellip;')
      .replace(/Scan läuft – Quick-Wins erscheinen nach Abschluss …/g, 'Daten werden ausgewertet — Hinweise erscheinen in wenigen Sekunden …')
      .replace(/Scan l&auml;uft – Quick-Wins erscheinen nach Abschluss &hellip;/g, 'Daten werden ausgewertet &mdash; Hinweise erscheinen in wenigen Sekunden &hellip;');
  }

  return html;
}
